/**
 * Recruiter → sent contact requests.
 *
 * Route: /recruiter/contacts (recruiter:read). The dashboard's "Recent
 * Contacts → View All" used to point here with no route behind it, which
 * bounced the recruiter to the home page. Reads GET /recruiter/contacts/sent.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useListSentContactRequestsQuery, type ContactRequestStatus } from '../store/api/recruiterApi';
import { LoadErrorNotice } from '../components/LoadErrorNotice';

const PAGE_SIZE = 20;

const FILTERS: Array<{ value: ContactRequestStatus | 'all'; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'declined', label: 'Declined' },
  { value: 'expired', label: 'Expired' },
];

const STATUS_STYLE: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  accepted: 'bg-green-100 text-green-800',
  declined: 'bg-red-100 text-red-800',
  expired: 'bg-gray-100 text-gray-800',
};

export function RecruiterContactsPage() {
  const [status, setStatus] = useState<ContactRequestStatus | 'all'>('all');
  const [page, setPage] = useState(0);
  const { data, isLoading, isFetching, isError, error, refetch } = useListSentContactRequestsQuery({
    status: status === 'all' ? undefined : status,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const lastPage = Math.max(0, Math.ceil(total / PAGE_SIZE) - 1);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/recruiter" className="text-sm text-primary-600 hover:underline">
            ← Recruiter dashboard
          </Link>
          <h1 className="mt-1 text-2xl font-bold text-gray-900">Contact Requests</h1>
          <p className="mt-1 text-gray-500">Requests you have sent to candidates, and their answers.</p>
        </div>
        <select
          aria-label="Filter by status"
          className="form-select"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as ContactRequestStatus | 'all');
            setPage(0);
          }}
        >
          {FILTERS.map((f) => (
            <option key={f.value} value={f.value}>
              {f.label}
            </option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <p className="text-gray-500">Loading…</p>
      ) : isError ? (
        <LoadErrorNotice
          error={error}
          onRetry={() => void refetch()}
          failedMessage="Your contact requests could not be loaded."
          testIdPrefix="recruiter-contacts"
        />
      ) : items.length === 0 ? (
        <div className="card p-6 text-center" data-testid="recruiter-contacts-empty">
          <p className="text-gray-500">
            {status === 'all' ? 'You have not sent any contact requests yet.' : 'No contact requests with this status.'}
          </p>
          <Link to="/marketplace" className="mt-2 inline-block text-sm text-primary-600 hover:underline">
            Find candidates in the Marketplace
          </Link>
        </div>
      ) : (
        <div className="card divide-y">
          {items.map((c) => (
            <div key={c.id} className="p-4 space-y-1">
              <div className="flex items-center justify-between gap-3">
                <p className="font-medium text-gray-900">
                  {c.roleTitle || 'No role specified'}
                  {c.companyName ? <span className="text-gray-500 font-normal"> · {c.companyName}</span> : null}
                </p>
                <span className={`px-2 py-0.5 text-xs rounded-full ${STATUS_STYLE[c.status] ?? STATUS_STYLE.pending}`}>
                  {c.status}
                </span>
              </div>
              <p className="text-sm text-gray-600 whitespace-pre-wrap">{c.message}</p>
              {c.responseMessage && (
                <p className="text-sm text-gray-700">
                  <span className="font-medium">Reply:</span> {c.responseMessage}
                </p>
              )}
              <p className="text-xs text-gray-500">
                Sent {new Date(c.createdAt).toLocaleDateString()}
                {c.respondedAt ? ` · answered ${new Date(c.respondedAt).toLocaleDateString()}` : ''}
                {c.status === 'pending' && c.expiresAt ? ` · expires ${new Date(c.expiresAt).toLocaleDateString()}` : ''}
              </p>
            </div>
          ))}
        </div>
      )}

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm">
          <button
            type="button"
            className="btn btn-sm"
            disabled={page === 0 || isFetching}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            Previous
          </button>
          <span className="text-gray-500">
            Page {page + 1} of {lastPage + 1}
          </span>
          <button
            type="button"
            className="btn btn-sm"
            disabled={page >= lastPage || isFetching}
            onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

export default RecruiterContactsPage;
